import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import settingsSvg from '../../../assets/images/icons/setting-3.svg'
import walletSvg from '../../../assets/images/icons/wallet-minus.svg'
import { useAnal, useDebounceValue, useNavigation } from '../../../hooks'
import { useModal } from '../../../hooks/useModal'
import { EModalName } from '../../../types/enums'
import { trpc } from '../../../utils'
import {
  Avatar,
  Button,
  CardButton,
  Input,
  InputFileWrapper,
} from '../../atoms'
import { GameSettings, TableSettings } from '../../molecules'
import { ModalAvatarEditor } from '../../organisms'
import {
  avatarBlock,
  h1,
  h2,
  headerStyle,
  iconStyle,
  nameStyle,
  pageStyle,
  profileInfo,
  settingsSection,
  unlinkWalletButton,
  versionBlock,
  walletConnected,
} from './index.css'

export const SettingsView = () => {
  const { data: userData, refetch: refetchUser } = trpc.user.getUser.useQuery(
    {},
  )
  const { mutate: updateUser } = trpc.user.updateUser.useMutation()
  const { mutateAsync: uploadAvatar } = trpc.user.uploadAvatar.useMutation()
  const { goToPage } = useNavigation()
  const { t } = useTranslation()
  const { logEvent } = useAnal()

  const userTonAddress = useTonAddress()
  const [tonConnectUi] = useTonConnectUI()
  const [userName, setUserName] = useState<string>('')
  const debouncedUserName = useDebounceValue(userName.trim(), 300)
  const { openModal, closeModal } = useModal()

  useEffect(() => {
    if (userData?.name) {
      setUserName(userData.name)
    }
  }, [userData])

  useEffect(() => {
    if (debouncedUserName && debouncedUserName !== userData?.name) {
      updateUser({ name: debouncedUserName })
    }
  }, [debouncedUserName, userData])

  useEffect(() => {
    logEvent('settings_opened')
  }, [])

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    openModal<'upload' | 'cancel'>(
      ({ close }) => (
        <ModalAvatarEditor
          image={file}
          close={async (base64?: string) => {
            if (!base64) {
              return
            }
            const response = await uploadAvatar({
              file: base64,
              fileName: file.name,
              fileType: file.type,
            })

            if (response.success) {
              await refetchUser()
              close('upload')
            } else {
              console.error('Failed to upload avatar')
            }
          }}
        />
      ),
      {
        title: t('modal.confirmation.avatarUpload.title'),
        position: 'center',
        closeOnClickOutside: true,
        name: EModalName.AVATAR_UPLOAD,
      },
    )
  }

  useEffect(() => {
    return () => {
      for (const modalName of [EModalName.AVATAR_UPLOAD]) {
        closeModal({
          name: modalName,
        })
      }
    }
  }, [])

  return (
    <div className={pageStyle}>
      <div className={headerStyle}>
        <img
          src={settingsSvg}
          className={iconStyle}
          alt='Settings Icon'
        />
        <h1 className={h1}>{t('settings.header')}</h1>
      </div>

      <div className={profileInfo}>
        <InputFileWrapper
          accept='image/*'
          onChangeFile={handleAvatarChange}
        >
          <div className={avatarBlock}>
            <Avatar avatar={userData?.avatar || null} />
          </div>
        </InputFileWrapper>
        <Input
          value={userName}
          pattern='([a-zA-Z0-9\-_]+ ?)+'
          className={nameStyle}
          onChange={(value) => setUserName(value || '')}
        />
      </div>

      <div className={settingsSection}>
        <h2 className={h2}>{t('settings.label.gameSettings')}</h2>
        <GameSettings />
      </div>

      <div className={settingsSection}>
        <h2 className={h2}>{t('settings.label.tableSettings')}</h2>
        <TableSettings />
      </div>

      <div className={settingsSection}>
        <h2 className={h2}>{t('settings.label.support')}</h2>
        <CardButton
          onClick={() => {
            window.open(import.meta.env.VITE_SUPPORT_BOT_URL as string)
          }}
        >
          {t('settings.button.chatWithUs')}
        </CardButton>

        <CardButton
          onClick={() => {
            goToPage('faq')
          }}
        >
          {t('settings.button.faq')}
        </CardButton>
      </div>

      {userTonAddress ? (
        <div className={walletConnected}>
          <Button
            variant='bordered'
            className={unlinkWalletButton}
            title={userTonAddress.slice(0, 14)}
            analytic={{
              name: 'unlink_wallet',
            }}
            onClick={() => {
              void tonConnectUi.disconnect()
            }}
          >
            <span>{t('settings.button.unlinkWallet')}</span>
            <img
              src={walletSvg}
              alt=''
            />
          </Button>
        </div>
      ) : null}

      <div className={versionBlock}>{t('settings.label.version')} 0.0.1</div>
    </div>
  )
}
